
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ThumbsUp, MessageSquare, UserCircle, BookmarkPlus } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import ArticleComments from "@/components/article/ArticleComments";
import ArticleRelated from "@/components/article/ArticleRelated";
import AskAIDialog from "@/components/article/AskAIDialog";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

interface ArticleFooterProps {
  articleId: string;
  likes: number;
  comments: number;
  author: {
    id?: string;
    name: string;
    subscriptionPrice: number;
  };
  articlePrice?: number;
}

const ArticleFooter: React.FC<ArticleFooterProps> = ({
  articleId,
  likes,
  comments,
  author,
  articlePrice = 4.99
}) => {
  const [showComments, setShowComments] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(likes);
  const [saved, setSaved] = useState(false);
  const { user } = useAuth();

  const handleLike = () => {
    if (liked) {
      setLikeCount(likeCount - 1);
    } else {
      setLikeCount(likeCount + 1);
    }
    setLiked(!liked);
  };

  const handleSave = () => {
    setSaved(!saved);
    
    toast({
      title: saved ? "Article removed from bookmarks" : "Article saved to bookmarks",
      description: saved ? "You can add it again anytime." : "View your saved articles in your profile.",
      duration: 3000,
    });
  };

  return (
    <footer className="max-w-3xl mx-auto px-4 md:px-0 mb-16">
      <Separator className="mb-8" />
      
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            className={`gap-2 ${liked ? 'bg-primary/10 text-primary' : ''}`}
            onClick={handleLike}
          >
            <ThumbsUp className={`h-4 w-4 ${liked ? 'fill-current' : ''}`} />
            {likeCount}
          </Button>
          
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => setShowComments(!showComments)}
          >
            <MessageSquare className="h-4 w-4" />
            {comments}
          </Button>
          
          <Button 
            variant="outline" 
            className={`gap-2 ${saved ? 'bg-primary/10 text-primary' : ''}`}
            onClick={handleSave}
          >
            <BookmarkPlus className={`h-4 w-4 ${saved ? 'fill-current' : ''}`} />
            {saved ? 'Saved' : 'Save'}
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <AskAIDialog articleId={articleId} />
          
          {author.id && (
            <Button variant="outline" asChild>
              <Link to={`/journalist/${author.id}`}>
                <UserCircle className="h-4 w-4 mr-2" />
                View Author Profile
              </Link>
            </Button>
          )}
        </div>
      </div>
      
      {showComments && (
        <div className="mb-12">
          <ArticleComments articleId={articleId} />
        </div>
      )}
      
      <Card className="bg-accent/50 mb-12">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-bold mb-2">Enjoy this article?</h3>
              <p className="text-muted-foreground">
                Support {author.name} directly by purchasing this article or subscribing for unlimited access.
              </p>
            </div>
            <div className="flex flex-col gap-3 w-full md:w-auto">
              {user && (
                <Button 
                  asChild 
                  className="w-full bg-directpress-orange text-white hover:bg-directpress-orange/90"
                >
                  <Link to="/subscription">
                    Subscribe now
                  </Link>
                </Button>
              )}
              {!user && (
                <Button asChild className="w-full md:w-auto">
                  <Link to="/auth">Sign in to purchase</Link>
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      <div>
        <h3 className="text-xl font-bold mb-6">Related Articles</h3>
        <ArticleRelated currentArticleId={articleId} />
      </div>
    </footer>
  );
};

export default ArticleFooter;
